import React, { useRef } from 'react';
import { KBBlock } from '../../../types';
import { knowledgeBaseAPI } from '../../../api/services';

interface Props {
  block: KBBlock;
  isEditing: boolean;
  onChange: (updates: Partial<KBBlock>) => void;
  onFinish: () => void;
}

const ImageBlock: React.FC<Props> = ({ block, isEditing, onChange, onFinish }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const url = block.data?.url ?? '';
  const caption = block.data?.caption ?? '';
  const alt = block.data?.alt ?? '';

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await knowledgeBaseAPI.uploadFile(file);
      onChange({ data: { ...block.data, url } });
    } catch {
      alert('Upload failed');
    }
  };

  if (isEditing) {
    return (
      <div className="space-y-3 p-4 border border-dashed border-gray-300 rounded-lg bg-gray-50">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Image URL</label>
          <input
            autoFocus
            type="url"
            value={url}
            placeholder="https://example.com/image.png"
            onChange={(e) => onChange({ data: { ...block.data, url: e.target.value } })}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">— or —</span>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="text-xs text-blue-600 hover:underline"
          >
            Upload from device
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Caption (optional)</label>
          <input
            type="text"
            value={caption}
            placeholder="Image caption"
            onChange={(e) => onChange({ data: { ...block.data, caption: e.target.value } })}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Alt text</label>
          <input
            type="text"
            value={alt}
            placeholder="Describe the image"
            onChange={(e) => onChange({ data: { ...block.data, alt: e.target.value } })}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          />
        </div>
        <button onClick={onFinish} className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded">Done</button>
      </div>
    );
  }

  if (!url) {
    return (
      <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center text-gray-400">
        <svg className="w-10 h-10 mx-auto mb-2 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <p className="text-sm">Image block — click to add URL or upload</p>
      </div>
    );
  }

  return (
    <figure className="text-center">
      <img src={url} alt={alt || caption || 'Image'} className="max-w-full rounded-lg mx-auto shadow-sm" />
      {caption && <figcaption className="mt-2 text-sm text-gray-500 italic">{caption}</figcaption>}
    </figure>
  );
};

export default ImageBlock;
